/* ============================================
   CoreIQ Accessibility Helpers
   Reusable utilities for dialogs, focus, and keyboard traps
   ============================================ */
(function () {
  'use strict';

  var FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function getFocusable(container) {
    if (!container) return [];

    return Array.prototype.slice
      .call(container.querySelectorAll(FOCUSABLE_SELECTOR))
      .filter(function (el) {
        return !el.hasAttribute('hidden') &&
          !el.getAttribute('aria-hidden') &&
          (el.offsetParent !== null || el === document.activeElement);
      });
  }

  function focusWithoutScroll(element) {
    if (!element || typeof element.focus !== 'function') return;
    try {
      element.focus({ preventScroll: true });
    } catch (e) {
      element.focus();
    }
  }

  function trapTabKey(event, container) {
    if (!event || event.key !== 'Tab') return false;

    var focusables = getFocusable(container);
    if (!focusables.length) {
      event.preventDefault();
      return true;
    }

    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    var active = document.activeElement;

    if (event.shiftKey && active === first) {
      event.preventDefault();
      focusWithoutScroll(last);
      return true;
    }

    if (!event.shiftKey && active === last) {
      event.preventDefault();
      focusWithoutScroll(first);
      return true;
    }

    return false;
  }

  function openDialog(dialog, options) {
    if (!dialog) return;

    options = options || {};
    var state = dialog.__ciqA11yState || {};

    state.lastActiveElement =
      options.trigger ||
      document.activeElement ||
      state.lastActiveElement ||
      null;

    dialog.__ciqA11yState = state;
    dialog.hidden = false;
    if ('inert' in dialog) {
      dialog.inert = false;
    } else {
      dialog.removeAttribute('inert');
    }

    if (options.lockBody !== false) {
      document.body.style.overflow = 'hidden';
    }

    var initialFocus = options.initialFocus || null;
    if (typeof initialFocus === 'string') {
      initialFocus = dialog.querySelector(initialFocus);
    }

    var focusables = getFocusable(dialog);
    // Reveal before moving focus. Focusing a descendant while its dialog is
    // still aria-hidden is ignored by some browsers and assistive technology.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        dialog.setAttribute('aria-hidden', 'false');
        if (initialFocus && typeof initialFocus.focus === 'function') {
          focusWithoutScroll(initialFocus);
        } else if (focusables.length) {
          focusWithoutScroll(focusables[0]);
        } else if (typeof dialog.focus === 'function') {
          focusWithoutScroll(dialog);
        }
      });
    });
  }

  function closeDialog(dialog, options) {
    if (!dialog) return;

    options = options || {};
    dialog.setAttribute('aria-hidden', 'true');
    if ('inert' in dialog) {
      dialog.inert = true;
    } else {
      dialog.setAttribute('inert', '');
    }

    var onAfterClose = function () {
      dialog.hidden = true;
      if (options.clearContainer && options.clearContainer.innerHTML !== undefined) {
        options.clearContainer.innerHTML = '';
      }
    };

    if (options.afterCloseDelayMs && options.afterCloseDelayMs > 0) {
      window.setTimeout(onAfterClose, options.afterCloseDelayMs);
    } else {
      onAfterClose();
    }

    if (options.lockBody !== false) {
      document.body.style.overflow = '';
    }

    var state = dialog.__ciqA11yState || {};
    var returnFocusTo =
      options.returnFocusTo ||
      state.lastActiveElement ||
      null;

    if (returnFocusTo && typeof returnFocusTo.focus === 'function') {
      focusWithoutScroll(returnFocusTo);
    }
  }

  window.CoreIQA11y = {
    getFocusable: getFocusable,
    trapTabKey: trapTabKey,
    openDialog: openDialog,
    closeDialog: closeDialog
  };
})();
